import type {
  ApiResponse,
  AuthPayload,
  User,
  Project,
  Script,
  Room,
  RoomCreateResponse,
  ResolveRoomResponse,
  ShortTokenInfo,
  InviteCode,
  AdminAccount,
  AdminSession,
  AiConfig,
  AsrConfig,
  AiSentence,
  AiFix,
  AiReplacement,
  AiSplitResult,
  AiReviewResult,
  AiOptimizeResult,
  AiRecordStatus,
  AiOptimizationRecord,
  GlossaryTerm,
} from "@/types";

const BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * v1.16 API Client
 * - 鉴权改为 httpOnly Cookie（access_token / refresh_token），前端不存 JWT
 * - fetch 统一 credentials: "include"
 * - 401 时单飞调用 /api/auth/refresh 后重放一次
 */
class ApiClient {
  private refreshing: Promise<string | null> | null = null;

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    let response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      credentials: "include",
      headers,
    });

    // 401 → 尝试刷新一次再重放（避免登录/刷新接口自身递归）
    if (
      response.status === 401 &&
      !endpoint.startsWith("/api/auth/login") &&
      !endpoint.startsWith("/api/auth/register") &&
      !endpoint.startsWith("/api/auth/refresh") &&
      !endpoint.startsWith("/api/admin/login")
    ) {
      const ok = await this.refreshAccessToken();
      if (ok) {
        response = await fetch(`${BASE_URL}${endpoint}`, {
          ...options,
          credentials: "include",
          headers,
        });
      }
    }

    let data: any = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      return {
        success: false,
        error: data?.message || data?.error || `HTTP ${response.status}`,
      };
    }
    return data || { success: true };
  }

  // 单飞刷新：并发 401 只触发一次 refresh
  private refreshAccessToken(): Promise<string | null> {
    if (!this.refreshing) {
      this.refreshing = fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
      })
        .then(async (r) => (r.ok ? "ok" : null))
        .catch(() => null)
        .finally(() => {
          this.refreshing = null;
        });
    }
    return this.refreshing;
  }

  // --- Auth ---
  async login(email: string, password: string) {
    return this.request<AuthPayload>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async register(data: {
    displayName: string;
    email: string;
    password: string;
    inviteCode: string;
  }) {
    return this.request<AuthPayload>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getMe() {
    return this.request<User>("/api/auth/me");
  }

  async logout() {
    return this.request<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
  }

  // --- Projects ---
  async getProjects() {
    return this.request<Project[]>("/api/projects");
  }

  async getProject(id: string) {
    return this.request<Project>(`/api/projects/${id}`);
  }

  async createProject(data: { name: string; description?: string }) {
    return this.request<Project>("/api/projects", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // --- Scripts ---
  async getScripts(projectId: string) {
    return this.request<Script[]>(`/api/projects/${projectId}/scripts`);
  }

  async getScript(id: string) {
    return this.request<Script>(`/api/scripts/${id}`);
  }

  async createScript(projectId: string, data: { title: string; content: string }) {
    return this.request<Script>(`/api/projects/${projectId}/scripts`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateScript(id: string, data: { title?: string; content?: string; splitContent?: string }) {
    return this.request<Script>(`/api/scripts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteScript(id: string) {
    return this.request<{ ok: boolean }>(`/api/scripts/${id}`, { method: "DELETE" });
  }

  // --- Rooms (v1.16) ---
  async getRooms(projectId?: string) {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    return this.request<Room[]>(`/api/rooms${qs}`);
  }

  async createRoom(data: {
    projectId: string;
    scriptId: string;
    name: string;
    mode?: "single" | "multi";
  }) {
    return this.request<RoomCreateResponse>("/api/rooms", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getRoom(id: string) {
    return this.request<Room>(`/api/rooms/${id}`);
  }

  // 短链接兑换：房间码 + 端点 → 短 token（公开，房间码即凭证）
  async getViewerToken(roomCode: string, role: "anchor" | "display" | "subtitle") {
    return this.request<{ token: string; projectId: string; roomCode: string; role: string; expiresAt: string }>(
      `/api/rooms/by-code/${encodeURIComponent(roomCode)}/token`,
      { method: "POST", body: JSON.stringify({ role }) }
    );
  }

  async getRoomByCode(roomCode: string) {
    return this.request<Room>(`/api/rooms/by-code/${encodeURIComponent(roomCode)}`);
  }

  async resolveRoom(roomId: string) {
    return this.request<ResolveRoomResponse>(`/api/rooms/resolve?roomId=${encodeURIComponent(roomId)}`);
  }

  async getRoomTokens(id: string) {
    return this.request<ShortTokenInfo[]>(`/api/rooms/${id}/tokens`);
  }

  async revokeToken(id: string, tokenId: string) {
    return this.request<{ ok: boolean }>(`/api/rooms/${id}/tokens/${tokenId}/revoke`, {
      method: "POST",
    });
  }

  // 切换房间同步文稿版本（原稿/断句版本），服务端广播到各端
  async setRoomScriptVersion(id: string, useSplitScript: boolean) {
    return this.request<{ room: Room; content: string }>(`/api/rooms/${id}/script-version`, {
      method: "PATCH",
      body: JSON.stringify({ useSplitScript }),
    });
  }

  // --- AI 流水线（v2.0：拆句 / Agent1 问题识别 / Agent2 替换建议）---
  async getAiConfig() {
    return this.request<AiConfig>("/api/ai/config");
  }

  async updateAiConfig(data: {
    llm?: { baseUrl?: string; model?: string; apiKey?: string };
    prompts?: { split?: string; agent1?: string; agent2?: string };
  }) {
    return this.request<AiConfig>("/api/ai/config", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // 大模型拆句：按口播节奏把文稿拆成短句（含说话人前缀绑定）
  async aiSplit(data: { content: string; mode?: "single" | "multi"; projectId?: string }) {
    return this.request<AiSplitResult>("/api/ai/split", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Agent 1：问题识别（返回问题句）
  async aiReview(data: { content: string; sentences?: AiSentence[] }) {
    return this.request<AiReviewResult>("/api/ai/review", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Agent 2：替换建议（replacement_text 含 \n 时拆多句）
  async aiOptimize(data: {
    content: string;
    sentences?: AiSentence[];
    issues?: AiSentence[];
    instructions?: string;
  }) {
    return this.request<AiOptimizeResult>("/api/ai/optimize", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiStatus() {
    return this.request<{ available: boolean; model: string; provider: string }>("/api/ai/status");
  }

  // 测试 LLM 连接（返回 ok/latencyMs/message 或结构化错误码）
  async testAiConnection() {
    return this.request<{ ok: boolean; latencyMs: number; message: string }>("/api/ai/config/test", {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  // --- 优化记录（v1.22：回溯底座）---
  async aiCreateRecord(data: {
    projectId: string;
    scriptId?: string;
    mode?: "single" | "multi";
    inputSentences: AiSentence[];
    issues: AiSentence[];
    replacements: AiReplacement[];
  }) {
    return this.request<AiOptimizationRecord>("/api/ai/records", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiListRecords(opts: { projectId?: string; scriptId?: string; limit?: number }) {
    const q = new URLSearchParams();
    if (opts.projectId) q.set("projectId", opts.projectId);
    if (opts.scriptId) q.set("scriptId", opts.scriptId);
    if (opts.limit) q.set("limit", String(opts.limit));
    return this.request<{ records: AiOptimizationRecord[] }>(`/api/ai/records?${q.toString()}`);
  }

  async aiUpdateRecord(
    id: string,
    data: { adoptedIndices?: number[]; appliedText?: string | null; status?: AiRecordStatus }
  ) {
    return this.request<AiOptimizationRecord>(`/api/ai/records/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // --- 热词库（v2.0，每项目一份）---
  async getGlossary(projectId: string) {
    return this.request<GlossaryTerm[]>(`/api/glossary?projectId=${encodeURIComponent(projectId)}`);
  }

  async createGlossaryTerm(data: { projectId: string; term: string; aliases?: string[] }) {
    return this.request<GlossaryTerm>("/api/glossary", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateGlossaryTerm(id: string, data: { term?: string; aliases?: string[] }) {
    return this.request<GlossaryTerm>(`/api/glossary/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteGlossaryTerm(id: string) {
    return this.request<{ ok: boolean }>(`/api/glossary/${id}`, { method: "DELETE" });
  }

  // --- 云端 ASR（v1.17 Fun-ASR-Flash）---
  async getAsrConfig() {
    return this.request<AsrConfig>("/api/asr/config");
  }

  async updateAsrConfig(data: { model?: string; apiKey?: string }) {
    return this.request<AsrConfig>("/api/asr/config", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async testAsrConnection() {
    return this.request<{ ok: boolean; latencyMs: number; message: string }>(
      "/api/asr/test",
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  async transcribeAsr(audioB64: string, sampleRate?: number) {
    return this.request<{ text: string; latencyMs: number; words: Array<{ text: string; begin_time?: number; end_time?: number }> }>(
      "/api/asr/transcribe",
      {
        method: "POST",
        body: JSON.stringify({ audio: audioB64, sampleRate }),
      }
    );
  }

// --- Admin (独立会话，9005 端口) ---
  async adminLogin(username: string, password: string) {
    return this.request<AdminSession>("/api/admin/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  }

  async adminMe() {
    return this.request<AdminSession>("/api/admin/me");
  }

  async adminLogout() {
    return this.request<{ ok: boolean }>("/api/admin/logout", { method: "POST" });
  }

  async adminListAccounts() {
    return this.request<AdminAccount[]>("/api/admin/accounts");
  }

  async adminListInvites() {
    return this.request<InviteCode[]>("/api/admin/invite-codes");
  }

  async adminCreateInvite(data: { maxUses: number; expiresDays?: number }) {
    return this.request<InviteCode>("/api/admin/invite-codes", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async adminRevokeInvite(id: string) {
    return this.request<{ ok: boolean }>(`/api/admin/invite-codes/${id}`, {
      method: "PUT",
      body: JSON.stringify({ maxUses: 0 }),
    });
  }
}

export const apiClient = new ApiClient();
